import { useState, useCallback, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import {layerGroups} from '../config/layers';

/**
 * Custom hook for managing map controls and layer visibility
 */
const useMapControls = ({ mapRef, fetchLayerData }) => {
  // Map state
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isLayerLoading, setIsLayerLoading] = useState(false);
  const [loadingLayerName, setLoadingLayerName] = useState('');
  const [shimmerAnimation, setShimmerAnimation] = useState(null);
  
  // Load base counties layer
  const loadBaseCountiesLayer = useCallback(async (map) => {
    try {
      const response = await fetch('/data/counties.geojson');
      if (!response.ok) {
        throw new Error('Failed to fetch base counties data');
      }
      const geojsonData = await response.json();

      // Add base counties source if it doesn't exist
      if (!map.getSource('base-counties')) {
        map.addSource('base-counties', {
          type: 'geojson',
          data: geojsonData
        });

        // Add line layer first
        map.addLayer({
          id: 'base-counties-line',
          type: 'line',
          source: 'base-counties',
          layout: {
            'visibility': 'visible'
          },
          paint: {
            'line-color': '#607D8B', // Medium blue-gray for base lines
            'line-width': 0.5,
            'line-opacity': 0.8
          }
        });

        // Add fill layer with enhanced shimmer effect
        map.addLayer({
          id: 'base-counties-fill',
          type: 'fill',
          source: 'base-counties',
          layout: {
            'visibility': 'visible'
          },
          paint: {
            'fill-color': '#ECEFF1', // Very light blue-gray for base fill
            'fill-opacity': 0.3, // Increased opacity for better visibility
            'fill-pattern': 'shimmer' // Add shimmer pattern
          }
        });

        // Create shimmer pattern
        const createShimmerPattern = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = 128;
          canvas.height = 128;
          
          // Create gradient
          const gradient = ctx.createLinearGradient(0, 0, 128, 128);
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
          gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.4)');
          gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.6)');
          gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.4)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, 128, 128);
          
          return ctx.getImageData(0, 0, 128, 128);
        };

        // Add shimmer pattern to map
        if (!map.hasImage('shimmer')) {
          map.addImage('shimmer', createShimmerPattern());
        }

        // Calculate bounds
        const bounds = new mapboxgl.LngLatBounds();
        geojsonData.features.forEach(feature => {
          if (feature.geometry && feature.geometry.coordinates) {
            if (feature.geometry.type === 'Polygon') {
              feature.geometry.coordinates.forEach(ring => {
                ring.forEach(coord => {
                  if (Array.isArray(coord) && coord.length >= 2) {
                    bounds.extend([coord[0], coord[1]]);
                  }
                });
              });
            } else if (feature.geometry.type === 'MultiPolygon') {
              feature.geometry.coordinates.forEach(polygon => {
                polygon.forEach(ring => {
                  ring.forEach(coord => {
                    if (Array.isArray(coord) && coord.length >= 2) {
                      bounds.extend([coord[0], coord[1]]);
                    }
                  });
                });
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading base counties layer:', error);
      // Fallback to default Texas view on error
      map.easeTo({
        center: [-99.5, 31.5],  // Center of Texas
        zoom: 5.15
      });
    }
  }, []);

  // Cleanup shimmer animation on unmount
  useEffect(() => {
    return () => {
      if (shimmerAnimation) {
        cancelAnimationFrame(shimmerAnimation);
      }
    };
  }, [shimmerAnimation]);

  // Handle map load
  const handleMapLoad = useCallback((e) => {
    setMapLoaded(true);
    const map = mapRef.current?.getMap();
    if (!map) return;

    if (map.isStyleLoaded()) {
      loadBaseCountiesLayer(map).then(() => {
        toggleLayer(layerGroups.counties.layers[0], true, true);
      });
    } else {
      map.once('load', () => {
        loadBaseCountiesLayer(map).then(() => {
          toggleLayer(layerGroups.counties.layers[0], true, true);
        });
      });
    }
  }, [loadBaseCountiesLayer]);

  // Toggle layer visibility
  const toggleLayer = useCallback(async (layer, isChecked, onload) => {
    const map = mapRef.current?.getMap();
    if (!map || isLayerLoading) return;

    try {
      if (!isChecked) {
        // Remove overlay layers if unchecked
        const layers = ['dmqt-layer-boundaries-fill', 'dmqt-layer-boundaries-lines', 'dmqt-layer-labels'];
        layers.forEach(layerId => {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        });
        if (map.getSource('dmqt-layer-boundaries')) {
          map.removeSource('dmqt-layer-boundaries');
        }
        return;
      }

      // Toggle visibility of overlay layers
      if (!isLayerLoading) {
        if(!onload){
          setIsLayerLoading(true);
        }
        setLoadingLayerName(layer.title);
        try {
          // First, ensure base counties layers exist and are visible
          if (!map.getSource('base-counties')) {
            await loadBaseCountiesLayer(map);
          }

          // Make sure base counties layers are visible
          if (map.getLayer('base-counties-fill')) {
            map.setLayoutProperty('base-counties-fill', 'visibility', 'visible');
          }
          if (map.getLayer('base-counties-line')) {
            map.setLayoutProperty('base-counties-line', 'visibility', 'visible');
          }

          // Load the selected layer data
          await fetchLayerData(layer);
          
          // Ensure overlay layers are on top of base layers
          const layers = ['dmqt-layer-boundaries-fill', 'dmqt-layer-boundaries-lines', 'dmqt-layer-labels'];
          layers.forEach(layerId => {
            if (map.getLayer(layerId)) {
              map.setLayoutProperty(layerId, 'visibility', 'visible');
              // Move overlay layers to the top
              map.moveLayer(layerId);
            }
          });
        } finally {
          setIsLayerLoading(false);
          setLoadingLayerName('');
        }
      }
    } catch (error) {
      console.error('Error toggling layer:', error);
    } finally {
      setIsLayerLoading(false);
      setLoadingLayerName('');
    }
  }, [mapRef, fetchLayerData, isLayerLoading, loadBaseCountiesLayer]);

  // Fit map to bounds of visible layers
  const fitMapToBounds = useCallback(() => {

    const map = mapRef.current?.getMap();
    if (!map || !mapLoaded) return;

    try {
      // Get all visible layers
      const visibleLayers = [];

      // Check county layers
      const countySource = map.getSource('dmqt-layer-boundaries');
      if (countySource && countySource._data) {
        visibleLayers.push(countySource._data);
      }

      // If no visible layers, return
      if (visibleLayers.length === 0) return;

      // Calculate bounds from all visible layers
      const bounds = new mapboxgl.LngLatBounds();

      visibleLayers.forEach(layer => {
        if (layer.type === 'FeatureCollection') {
          layer.features.forEach(feature => {
            if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
              const coords = feature.geometry.type === 'Polygon' ?
                [feature.geometry.coordinates] : feature.geometry.coordinates;

              coords.forEach(polygon => {
                polygon.forEach(ring => {
                  ring.forEach(coord => {
                    bounds.extend(coord);
                  });
                });
              });
            }
          });
        } else if (layer.type === 'Feature') {
          if (layer.geometry.type === 'Polygon' || layer.geometry.type === 'MultiPolygon') {
            const coords = layer.geometry.type === 'Polygon' ?
              [layer.geometry.coordinates] : layer.geometry.coordinates;

            coords.forEach(polygon => {
              polygon.forEach(ring => {
                ring.forEach(coord => {
                  bounds.extend(coord);
                });
              });
            });
          }
        }
      });



      // Fit map to bounds with padding
      map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        linear: false, // Use fly animation for smoother transition
        duration: 1000 // Animation duration in milliseconds
      });


    } catch (error) {
      console.error('Error fitting map to bounds:', error);
    }
  }, [mapRef, mapLoaded]);

  // Reset map view
  const resetMapView = useCallback(() => {
    // Check if mapRef and mapLoaded are available
    if (!mapRef.current || !mapLoaded) {
      console.warn("Map not loaded yet, cannot reset view");
      return;
    }

    try {
      const map = mapRef.current.getMap();

      // Additional check to ensure map is valid
      if (!map) {
        console.warn("Map instance is null, cannot reset view");
        return;
      }

      // Reset to default view
      map.easeTo({
        center: [-99.5, 31.5],  // Center of Texas
        zoom: 5.15
      });
    } catch (error) {
      console.error("Error resetting map view:", error);
    }
  }, [mapRef, mapLoaded]);

  return {
    mapLoaded,
    isLayerLoading,
    loadingLayerName,
    handleMapLoad,
    toggleLayer,
    resetMapView,
    fitMapToBounds
  };
};

export default useMapControls;
